/*
 * Решение человека о статистике и опознаватель визита.
 *
 * Здесь нет ни одного обращения к сети и ни одного трогания страницы — только
 * чтение и запись решения. Так эту часть можно проверить тестом целиком, а
 * проверять её стоит: от неё зависит, появится ли `visit_id` вообще.
 *
 * Опознаватель не существует до согласия физически. Не «есть, но не
 * отправляется» — его просто нет в хранилище браузера.
 */

export type ConsentChoice = "granted" | "denied";

/*
 * Редакция того, о чём спрашивали. Меняется только вместе с тем, что
 * собирается: старое согласие относится к старому вопросу, и молча
 * распространять его на новый сбор нельзя.
 */
export const CONSENT_REVISION = "2026-09-12.2";

export const CONSENT_KEY = "bb.consent.analytics";
const VISIT_KEY = "bb.visit";
const VISIT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface StoredConsent { choice?: unknown; revision?: unknown; at?: unknown }
interface StoredVisit { id?: unknown; until?: unknown }

/* Приватное окно, запрет на хранилище, полный диск — всё это не повод падать. */
function read(store: KeyValueStore | null, key: string): unknown {
  if (!store) return null;
  try {
    const raw = store.getItem(key);
    return raw ? JSON.parse(raw) as unknown : null;
  } catch { return null; }
}

function write(store: KeyValueStore | null, key: string, value: unknown): void {
  if (!store) return;
  try { store.setItem(key, JSON.stringify(value)); } catch { /* без хранилища спросим снова */ }
}

/** `null` — вопрос ещё не задавали или отвечали на другую редакцию. */
export function readConsent(store: KeyValueStore | null, revision = CONSENT_REVISION, now = Date.now()): ConsentChoice | null {
  const stored = read(store, CONSENT_KEY) as StoredConsent | null;
  // Rejection remains valid when another vendor is added. Acceptance never expands silently.
  if (!stored || (stored.choice !== "denied" && stored.revision !== revision)) return null;
  const at = typeof stored.at === "string" ? Date.parse(stored.at) : NaN;
  if (!Number.isFinite(at) || at > now || now - at > 180 * 86400000) return null;
  return stored.choice === "granted" || stored.choice === "denied" ? stored.choice : null;
}

export function writeConsent(
  store: KeyValueStore | null,
  choice: ConsentChoice,
  now: number = Date.now(),
  revision = CONSENT_REVISION
): void {
  write(store, CONSENT_KEY, { choice, revision, at: new Date(now).toISOString() });
  // Отказ — это ещё и требование убрать то, что уже сохранено.
  if (choice === "denied" && store) {
    try { store.removeItem(VISIT_KEY); } catch { /* уже недоступно */ }
  }
}

export function forgetConsent(store: KeyValueStore | null): void {
  if (!store) return;
  try { store.removeItem(CONSENT_KEY); store.removeItem(VISIT_KEY); } catch { /* нечего забывать */ }
}

/** 128 бит из криптостойкого источника. Ничего производного от устройства. */
export function randomId(view: { crypto?: Crypto }): string {
  const bytes = new Uint8Array(16);
  if (!view.crypto?.getRandomValues) throw new Error("Secure random identifiers unavailable");
  view.crypto.getRandomValues(bytes);
  return [...bytes].map(byte => byte.toString(16).padStart(2, "0")).join("");
}

export interface VisitIdentity { id: string; returning: boolean }

export function storedVisitId(store: KeyValueStore | null): string | null {
  const stored = read(store, VISIT_KEY) as StoredVisit | null;
  return typeof stored?.id === "string" && /^[0-9a-f]{32}$/.test(stored.id) ? stored.id : null;
}

/*
 * Опознаватель визита: один человек на одном устройстве, тридцать дней.
 *
 * Срок продлевается при каждом приходе, а не отсчитывается от первого: иначе
 * постоянный читатель через месяц превращается в нового человека, и вся
 * возвратность в отчётах оказывается выдумкой.
 */
export function visitIdentity(
  store: KeyValueStore | null,
  view: { crypto?: Crypto },
  now: number = Date.now()
): VisitIdentity {
  const stored = read(store, VISIT_KEY) as StoredVisit | null;
  const valid = typeof stored?.id === "string"
    && /^[0-9a-f]{32}$/.test(stored.id)
    && typeof stored.until === "number"
    && stored.until > now;
  const id = valid ? stored!.id as string : randomId(view);
  write(store, VISIT_KEY, { id, until: now + VISIT_TTL_MS });
  return { id, returning: valid };
}
